/*! *****************************************************************************
File: Data-grid.template.ts
Author: Jared Craig - jared.craig@gm.com
Date: 10-10-2017
Description: The Data Grid Template stores and maintains the state and
 configuration details used with the Data Grid
Other contributors: Neel Radia
***************************************************************************** */

import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { messages } from './data-grid.config';
import { Injector } from '@angular/core';

export abstract class DataGridTemplate {
  editDataItem: any = {};
  formBuilder: UntypedFormBuilder;
  data: any[];
  modal: boolean;
  changed: Subject<any> = new BehaviorSubject<any>(null);
  httpResponse: Subject<any> = new BehaviorSubject<any>(null);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  detailTemplate: DataGridTemplate | any = undefined;
  detailTitleField: string = undefined;
  displayField: string = undefined;
  title: string = undefined;
  filterable: string | boolean;
  columnMenu = true;
  groupable: boolean;
  resizable: boolean;
  reorderable: boolean;
  sortable = true;
  autoLock: boolean;
  isNotificationsEnabled = true;
  isSelectAll: boolean;
  showSelectAll = true;
  editedRowIndex: any;
  isEditing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  fileName: string = undefined;
  selectedRows: any[] = [];
  isServerSideFiltering: boolean;
  isClickEdit = false;
  isSubmitOnEnter = true;
  sender: any;
  collapseOnGroup = false;
  editOnselect = false;
  height: any = 500;
  saveUserSettings = true;
  selectableSettings = {
    enabled: false,
    checkboxOnly: true,
    mode: 'multiple'
  };
  selectColumnLocked = false;
  isCheckboxDisabled = false;
  pageable: any = {
    buttonCount: 5,
    info: true,
    pageSizes: [10, 25, 50, 100] as any[]
  };
  isAll: boolean;
  appCardTitle: string;

  /**
   * The controls used by the grid
   * 'add' 'edit', 'delete', and 'excel' are default grid events with built in callbacks and are enabled by default
   * To desable a default control, set the control to false during initialization
   *
   * ### Example
   *
   * ```
   * ngOnInit() {
   *  this.controls.delete = false
   * }
   * ```
   *
   *
   * 'custom' and 'customToolbar' are lists of buttons with a custom callback.
   *
   * ### Example
   *
   * ```
   * myCustomControl = {
   *   id: "myCustomControl",
   *   iconClass: "fa fa-cogs",
   *   title: "Custom description on button hover",
   *   text: "Custom Button Label",
   *   callback: this.handleMyCustomControl,
   * }
   *
   * this.controls.custom = [
   *   myCustomControl,
   * ]
   *
   * or
   *
   * this.controls.customToolbar = [
   *   myCustomControl,
   * ]
   * ```
   */
  controls = {
    add: true,
    excel: true,
    edit: true,
    delete: true,
    configure: false,
    customButtons: [],
    customToolBarButtons: [],
    customToolBarOptions: [],
    toolbarToggle: null,
    dropdownFilters: [],
    width: null,
    export: false,
    locked: false
  };
  /**
   * The state of the grid
   */
  gridState: any = {
    sort: [],
    skip: 0,
    take: 50 as any,
    group: []
  };
  /**
   *
   */
  recordCount: number;
  /**
   * The customization properties for each column displayed in the grid
   *
   * ### Example
   *
   * ```
   * columns = [
   * {
   *   field: "ProductID",
   *   title: "Id",
   *   editable: false,
   *   width: 50
   * },
   * {
   *   field: "ProductName",
   *   title: "Name",
   * },
   * {
   *   field: "UnitPrice",
   *   title: "Unit Price",
   *   editor: "numeric",
   * }
   * ]
   * ```
   *
   */
  abstract columns: any[];
  /**
   * The reactive form to track the state and validity while editing
   *
   * ### Example
   *
   * ```
   * editForm = this.formBuilder.group({
   *   ProductID: [],
   *   ProductName: ["", Validators.required],
   *   UnitPrice: [0]
   * })
   * ```
   *
   */
  abstract editForm: UntypedFormGroup;

  constructor(private readonly injector: Injector) {
    this.formBuilder = this.injector.get(UntypedFormBuilder);
  }
  /**
   * The Data Grid (Read) event
   * @param force - if true, read should return latest data instead of cached
   */
  abstract read(opts: any): Promise<any>;
  /**
   * The Data Grid (Add New) event
   * @param parentEditDataItem - detail row parent editDataItem
   */
  abstract create(parentEditDataItem: any): void;
  /**
   * The Data Grid (Edit) event
   * @param dataItem - Edit dataItem for selected grid row
   */
  abstract edit(dataItem: any): void;
  /**
   * The Data Grid (Save) event
   * @param dataItem - value from editForm
   */
  abstract save(dataItem: any, isNew: boolean): Promise<any>;

  /**
   * The Data Grid (Delete) event
   * @param dataItem - Record to be deleted
   */
  abstract delete(dataItem: any): Promise<any>;

  /**
   *
   */
  submit = (dataItem: any, isNew: any): Promise<any> => null;
  /**
   *
   */
  excel = (): Promise<any> => null;

  /**
   * Implementaion for disabling controls on the grid - (disabled)
   * Override to return the state the controls.  Default is false (enabled).
   * @param id - unique id for the control e.g('edit', 'add', 'myCustomButton')
   * @param dataItem - dataItem for the selected row/record
   */
  readonly isDisabled = (id: string, dataItem: any): boolean => false;

  /**
   * Implementaion for enabling controls on the grid  (hidden)
   * Override to return the state the controls.  Default is true (visible).
   * @param id - unique id for the control e.g('edit', 'add', 'myCustomButton')
   * @param dataItem - dataItem for the selected row/record
   */
  readonly isEnabled = (id: string, dataItem: any): boolean => true;

  /**
   *
   */
  readonly isShowDetail = (dataItem: any): boolean => true;

  /**
   * Data Grid callback when the grid selection changes.
   */
  select = (data: any): void => null;

  /**
   * Manually refresh the grid
   */
  refresh = () => this.changed.next(null);

  /**
   *
   */
  onCreate = (parentEditDataItem: any): void => {
    this.editDataItem = {};
    this.create(parentEditDataItem);
    this.editForm.reset(this.editDataItem);
  }

  /**
   *
   */
  onEdit = (dataItem: any, rowIndex): void => {
    this.editDataItem = dataItem;
    this.editForm.reset(dataItem);
    this.editedRowIndex = rowIndex;
    this.edit(dataItem);
  }

  /**
   * Callback for save requests
   * Take response from the implementation of the save and build a response message.
   * Construct and send the response message to the Data Grid
   * @param dataItem - value of the form
   * @param isNew - create or update
   */
  onSave = ({ dataItem, isNew, isSubmit = false }) => {
    const response: Promise<any> = isSubmit
      ? this.submit(dataItem, isNew)
      : this.save(dataItem, isNew);

    const message =
      this.getSource(dataItem) +
      (isNew
        ? isSubmit
          ? messages.recordSubmitted
          : messages.recordCreated
        : isSubmit
          ? messages.recordSubmitted
          : messages.recordUpdated);

    if (this.isNotificationsEnabled) {
      this.httpResponse.next({
        response,
        message
      } as IHttpResponse);
    }

    this.editedRowIndex = undefined;
    this.editDataItem = undefined;
  }

  /**
   * Check if displayField is set, then fetch the value for the displayField in dataItem
   * Return the fetched value if it is valid, otherwise return 'Record' (default)
   * @param dataItem - focused dataItem/record
   */
  getSource = (dataItem: any): string => {
    const source: string = this.displayField
      ? dataItem[this.displayField]
      : 'Record';
    return source ? source : 'Record';
  }

  getDeleteMessage = (dataItem: any): string => undefined;
  readonly canEdit = (dataItem: any): boolean => true;

}

/**
 *
 */
export class GridButton {
  id: string;
  iconClass: string;
  text: string;
  title: string;
  bare: boolean;
  togglable: boolean;
  primary: boolean;
  selected: boolean;
  refresh = true;
  callback: () => null;
}
/**
 *
 */
abstract class GridColumnTemplate {
  type: string;
  field: string;
  title: string;
  width?: number;
  hidden?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  template?: any;
  detail?: any;
}
/**
 *
 */
export class GridComboBox extends GridColumnTemplate {
  data: any[];
  textField: string;
  valueField: string;
  placeholder: string;
}
/**
 *
 */
export class GridBoolean extends GridColumnTemplate {
  onLabel: string;
  offLabel: string;
}
/**
 *
 */
interface IHttpResponse {
  response: Promise<any>;
  message: string;
}

export const flatten = filter => {
  const filters = (filter || {}).filters;
  if (filters) {
    return filters.reduce(
      (acc, curr) => acc.concat(curr.filters ? flatten(curr) : [curr]),
      []
    );
  }
  return [];
};

export const matches = (el, selector) =>
  (el.matches || el.msMatchesSelector).call(el, selector);
